// /////////////////////////////////////////////////////////////
// [ファイル] BuildingForm.tsx
// [概要] 建物住所タブの建物 登録/編集 ダイアログ。
//        buildingId 無し=新規登録、有り=既存建物の編集 (詳細を取得して初期表示)。
//        保存は upsert_crm_building へ送信し、成功時に onSuccess(buildingId) を返す。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import AddressAutocomplete from './AddressAutocomplete';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const UPSERT_URL = ep('upsert_crm_building');
const DETAIL_URL = ep('get_crm_building_detail');

// 建物種別の選択肢 (フェーズ1はプルダウン固定)
export const BUILDING_TYPE_OPTIONS = ['マンション', 'ビル', 'アパート', '戸建', '倉庫', 'その他'];

interface BuildingFormProps {
  buildingId?: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: (buildingId: string) => void;
}

interface BuildingFormData {
  building_name: string;
  building_name_kana: string;
  postal_code: string;
  address_main: string;
  building_type: string;
  structure: string;
  total_floors: string;
  built_year_month: string;
  notes: string;
  registration_source: string;
}

const EMPTY_FORM: BuildingFormData = {
  building_name: '',
  building_name_kana: '',
  postal_code: '',
  address_main: '',
  building_type: '',
  structure: '',
  total_floors: '',
  built_year_month: '',
  notes: '',
  registration_source: 'manual',
};

// 上段にラベル(グレー)、下段に入力欄の共通レイアウト
const LabeledField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  required,
  children,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="caption" sx={{ color: 'grey.600', fontSize: '0.75rem', lineHeight: 1.4 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
      )}
    </Typography>
    {children}
  </Box>
);

export default function BuildingForm({ buildingId, open, onClose, onSuccess }: BuildingFormProps) {
  const { user } = useAuth();
  const notify = useNotification();

  const [formData, setFormData] = useState<BuildingFormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  // 読込時点で簡易登録だったか (昇格チェックボックスの表示判定。チェック操作で消えないよう別 state で保持)
  const [wasCustomerAuto, setWasCustomerAuto] = useState(false);

  // 既存建物の編集時は詳細を取得して初期表示
  useEffect(() => {
    if (!open) return;
    setNameError(false);
    setWasCustomerAuto(false);
    if (!buildingId) {
      setFormData(EMPTY_FORM);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(DETAIL_URL, { buildingId });
        if (!cancelled && res.data.status === 'success') {
          const d = res.data.detail;
          setFormData({
            building_name: d.building_name || '',
            building_name_kana: d.building_name_kana || '',
            postal_code: d.postal_code || '',
            address_main: d.address_main || '',
            building_type: d.building_type || '',
            structure: d.structure || '',
            total_floors: d.total_floors != null ? String(d.total_floors) : '',
            built_year_month: d.built_year_month || '',
            notes: d.notes || '',
            registration_source: d.registration_source || 'manual',
          });
          setWasCustomerAuto(d.registration_source === 'customer_auto');
        }
      } catch (e) {
        console.error('BuildingForm detail fetch error:', e);
        if (!cancelled) notify.error('建物情報の取得に失敗しました');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // notify は Provider 再レンダーごとに識別子が変わるため依存に含めない (再フェッチループ防止)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId, open]);

  const handleChange = (field: keyof BuildingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'building_name' && value.trim()) setNameError(false);
  };

  const handleSave = async () => {
    // 建物名か住所のどちらかがあれば登録可 (名称未入力は住所から自動命名する簡易登録)
    if (!formData.building_name.trim() && !formData.address_main.trim()) {
      setNameError(true);
      notify.error('建物名称または住所のどちらかは必須です');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(UPSERT_URL, {
        buildingId: buildingId || null,
        data: {
          ...formData,
          building_name: formData.building_name.trim(),
          total_floors: formData.total_floors !== '' ? parseInt(formData.total_floors, 10) : null,
          operator_id: user?.email,
        },
      });
      if (res.data.status === 'success') {
        notify.success(res.data.message || '保存しました');
        onSuccess(res.data.buildingId || buildingId || '');
      } else {
        notify.error(res.data.message || '保存に失敗しました');
      }
    } catch (e) {
      console.error('BuildingForm save error:', e);
      notify.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '0.875rem',
          fontWeight: 700,
          py: 0.75,
          bgcolor: '#f1f5f9',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Icons.HomeWork sx={{ fontSize: 18, color: '#2e7d32' }} />
        {buildingId ? '建物情報の編集' : '新規建物登録'}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {loading ? (
          <LoadingView message="建物情報を取得中..." height="40vh" />
        ) : (
          <Grid container spacing={2}>
            <Grid size={8}>
              <LabeledField label="建物名称">
                <TextField
                  fullWidth
                  size="small"
                  value={formData.building_name}
                  error={nameError}
                  onChange={(e) => handleChange('building_name', e.target.value)}
                  placeholder="例: 保全オールビル"
                  helperText={!buildingId && !formData.building_name.trim() ? '戸建等で名称が無い場合は空欄のまま登録できます（住所から自動命名・簡易登録）' : undefined}
                />
              </LabeledField>
            </Grid>
            <Grid size={4}>
              <LabeledField label="建物名称カナ">
                <TextField
                  fullWidth
                  size="small"
                  value={formData.building_name_kana}
                  onChange={(e) => handleChange('building_name_kana', e.target.value)}
                  placeholder="例: ホゼンオールビル"
                />
              </LabeledField>
            </Grid>
            <Grid size={3}>
              <LabeledField label="郵便番号">
                <TextField
                  fullWidth
                  size="small"
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="例: 812-0011"
                />
              </LabeledField>
            </Grid>
            <Grid size={9}>
              <LabeledField label="住所">
                <AddressAutocomplete
                  mode="warn"
                  value={formData.address_main}
                  onTextChange={(text) => handleChange('address_main', text)}
                  excludeBuildingId={buildingId}
                  placeholder="例: 福岡県福岡市博多区博多駅前1-1-1"
                />
              </LabeledField>
            </Grid>
            <Grid size={3}>
              <LabeledField label="種別">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.building_type}
                  onChange={(e) => handleChange('building_type', e.target.value)}
                >
                  <MenuItem value="">
                    <em>未選択</em>
                  </MenuItem>
                  {BUILDING_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </LabeledField>
            </Grid>
            <Grid size={5}>
              <LabeledField label="構造">
                <TextField
                  fullWidth
                  size="small"
                  value={formData.structure}
                  onChange={(e) => handleChange('structure', e.target.value)}
                  placeholder="例: 鉄筋コンクリート造陸屋根10階建"
                />
              </LabeledField>
            </Grid>
            <Grid size={2}>
              <LabeledField label="階数">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formData.total_floors}
                  onChange={(e) => handleChange('total_floors', e.target.value)}
                  placeholder="例: 10"
                />
              </LabeledField>
            </Grid>
            <Grid size={2}>
              <LabeledField label="築年月">
                <TextField
                  fullWidth
                  size="small"
                  value={formData.built_year_month}
                  onChange={(e) => handleChange('built_year_month', e.target.value)}
                  placeholder="例: 平成8年3月"
                />
              </LabeledField>
            </Grid>
            <Grid size={6}>
              {buildingId && wasCustomerAuto && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
                  <Chip
                    label="簡易登録（顧客住所から自動作成）"
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100', alignSelf: 'flex-start' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.registration_source === 'manual'}
                        onChange={(e) => handleChange('registration_source', e.target.checked ? 'manual' : 'customer_auto')}
                      />
                    }
                    label={<Typography variant="caption">手動管理対象へ昇格する（建物一覧の既定表示に含める）</Typography>}
                  />
                </Box>
              )}
            </Grid>
            <Grid size={12}>
              <LabeledField label="備考">
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </LabeledField>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
          disabled={saving || loading}
        >
          {saving ? '保存中...' : '保存する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
